import { CollectionView } from "@bitwarden/common/admin-console/models/collections";
import { Organization } from "@bitwarden/common/admin-console/models/domain/organization";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { CollectionId, OrganizationId } from "@bitwarden/common/types/guid";
import {
  CipherViewLike,
  CipherViewLikeUtils,
} from "@bitwarden/common/vault/utils/cipher-view-like-utils";
import { isGuid } from "@bitwarden/guid";

import { VaultNavItemType, VaultsNavViewModel } from "./vault-nav-view-model";

/**
 * The `:vaultId` route segment for the personal vault.
 *
 * Deliberately not the vault table's `MY_VAULT` chip sentinel: that value is an internal filter
 * key, while this one is a URL a user can bookmark and share.
 */
export const MY_VAULT_ROUTE = "my-vault";

/**
 * The route segment naming an organization's "My items" collection — the default user collection
 * an organization under the data ownership policy gives each of its members — nested under its
 * `:vaultId`.
 *
 * A URL names the collection by this segment rather than by its id, for the same reason
 * {@link MY_VAULT_ROUTE} exists: the nav offers one "My items" entry per organization, and the id
 * behind it differs per member, so only the segment is a link that can be written down — see
 * {@link resolveVaultScope}, which trades it for the id.
 */
export const MY_ITEMS_ROUTE = "my-items";

/**
 * The route segment naming an organization vault's shared folders list, nested under its
 * `:vaultId`. The folders themselves nest one deeper still, as `:collectionId` beneath it, so the
 * URL says which page a drill-in was reached from. Lives here rather than beside the routes it
 * declares so the side nav can link to the page without reaching into an app from a library.
 */
export const SHARED_FOLDERS_ROUTE = "shared-folders";

/** The `:vaultId` route segment for trashed items. */
export const TRASH_ROUTE = "trash";

/** The `:vaultId` route segment for archived items. */
export const ARCHIVE_ROUTE = "archive";

export const VaultScopeType = Object.freeze({
  AllItems: "allItems",
  MyVault: "myVault",
  Organization: "organization",
  Trash: "trash",
  Archive: "archive",
} as const);
export type VaultScopeType = (typeof VaultScopeType)[keyof typeof VaultScopeType];

/**
 * The collection a URL drills an organization vault into: its id outright, or
 * {@link MY_ITEMS_ROUTE} for the organization's "My items" collection, whose id the URL cannot
 * name. {@link resolveVaultScope} trades the sentinel for the id the nav holds; every function
 * that narrows by a collection compares ids, so an unresolved sentinel matches nothing.
 */
export type ScopedCollectionId = CollectionId | typeof MY_ITEMS_ROUTE;

/**
 * The destination the side nav has narrowed the page to, independent of the table's own filter
 * chips.
 *
 * The nav presents these as siblings, so a scope covers both dimensions the destinations vary in:
 * which vault an item belongs to, and what state it is in. Trash and Archive span every vault,
 * the way All items does — see {@link cipherInScope}.
 *
 * An organization vault carries a third dimension: the shared folder the page has drilled into,
 * which the nav has no entry for but the URL names — see {@link parseVaultScope}.
 */
export type VaultScope =
  | { type: typeof VaultScopeType.AllItems }
  | { type: typeof VaultScopeType.MyVault }
  | {
      type: typeof VaultScopeType.Organization;
      organizationId: OrganizationId;
      /** The shared folder in view, when the URL drills into one. */
      collectionId?: ScopedCollectionId;
    }
  | { type: typeof VaultScopeType.Trash }
  | { type: typeof VaultScopeType.Archive };

export const ALL_ITEMS_SCOPE: VaultScope = { type: VaultScopeType.AllItems };

/** The page an organization vault scope names: its whole vault, "My items", or a shared folder. */
export const OrganizationVaultPage = Object.freeze({
  AllVaultItems: "allVaultItems",
  MyItems: "myItems",
  SharedFolder: "sharedFolder",
} as const);
export type OrganizationVaultPage =
  (typeof OrganizationVaultPage)[keyof typeof OrganizationVaultPage];

/**
 * Classifies an organization vault scope by the page it names — `undefined` for any other scope.
 *
 * A "My items" scope arrives either as the {@link MY_ITEMS_ROUTE} sentinel or, once
 * {@link resolveVaultScope} has traded it, as the collection's id, so both forms are matched.
 */
export function organizationVaultPage(
  scope: VaultScope,
  nav: VaultsNavViewModel | undefined,
): OrganizationVaultPage | undefined {
  if (scope.type !== VaultScopeType.Organization) {
    return undefined;
  }

  const { collectionId, organizationId } = scope;
  if (collectionId == null) {
    return OrganizationVaultPage.AllVaultItems;
  }

  if (
    collectionId === MY_ITEMS_ROUTE ||
    collectionId === defaultUserCollectionId(organizationId, nav)
  ) {
    return OrganizationVaultPage.MyItems;
  }

  return OrganizationVaultPage.SharedFolder;
}

/** The title key for each organization page — `null` for a shared folder, which the breadcrumb titles. */
const ORGANIZATION_PAGE_TITLE_KEYS: Record<OrganizationVaultPage, string | null> = {
  [OrganizationVaultPage.AllVaultItems]: "allVaultItems",
  [OrganizationVaultPage.MyItems]: "myItemsV2",
  [OrganizationVaultPage.SharedFolder]: null,
};

/**
 * The i18n key titling a scope's page. `null` for a shared folder, whose title is the folder name
 * the breadcrumb trail promotes to the heading rather than a fixed string.
 */
function vaultScopeTitleKey(scope: VaultScope, nav: VaultsNavViewModel | undefined): string | null {
  switch (scope.type) {
    case VaultScopeType.MyVault:
      return "myVault";
    case VaultScopeType.Trash:
      return "trash";
    case VaultScopeType.Archive:
      return "archiveNoun";
    case VaultScopeType.Organization: {
      const page = organizationVaultPage(scope, nav);
      return page == null ? null : ORGANIZATION_PAGE_TITLE_KEYS[page];
    }
    default:
      return "allItems";
  }
}

/**
 * The page title for a scope: a localized string, or `undefined` for a shared folder whose title
 * the breadcrumb trail supplies.
 */
export function vaultScopeTitle(
  scope: VaultScope,
  i18nService: I18nService,
  nav: VaultsNavViewModel | undefined,
): string | undefined {
  const key = vaultScopeTitleKey(scope, nav);
  return key == null ? undefined : i18nService.t(key);
}

/** The scopes named by a fixed route segment rather than an organization id. */
const NAMED_SCOPES = new Map<string, VaultScope>([
  [MY_VAULT_ROUTE, { type: VaultScopeType.MyVault }],
  [TRASH_ROUTE, { type: VaultScopeType.Trash }],
  [ARCHIVE_ROUTE, { type: VaultScopeType.Archive }],
]);

/**
 * Reads the vault and collection segments of a vault route — see `scopedCollectionSegment` for
 * where the latter comes from. An absent vault segment is "All items"; anything that is neither a
 * {@link NAMED_SCOPES} segment nor a guid names no destination and yields `null`.
 *
 * The collection segment drills the vault into one of its shared folders, or into
 * {@link MY_ITEMS_ROUTE}. Only an organization vault can be drilled into: a collection belongs to
 * an organization, and the vault a personal one, Trash, or the Archive would drill into is that
 * organization's rather than their own — so a collection segment alongside any other scope names no
 * destination and yields `null` too.
 *
 * Whether a guid names an organization the user is actually a member of, or a collection within
 * it, is left to `vaultScopeGuard` — resolving that needs the org list, and making every caller
 * await it would flash the unscoped vault while the list loads. The same goes for whether an
 * organization has a "My items" collection at all, which only those under data ownership do.
 */
export function parseVaultScope(
  segment: string | null | undefined,
  collectionSegment?: string | null,
): VaultScope | null {
  const scope = parseVaultSegment(segment);

  if (collectionSegment == null) {
    return scope;
  }

  if (scope?.type !== VaultScopeType.Organization || !isScopedCollectionId(collectionSegment)) {
    return null;
  }

  return { ...scope, collectionId: collectionSegment };
}

/** Whether a collection segment names a collection at all — see {@link ScopedCollectionId}. */
function isScopedCollectionId(segment: string): segment is ScopedCollectionId {
  return segment === MY_ITEMS_ROUTE || isGuid(segment);
}

/** The `:vaultId` segment on its own — see {@link parseVaultScope}. */
function parseVaultSegment(segment: string | null | undefined): VaultScope | null {
  if (segment == null) {
    return ALL_ITEMS_SCOPE;
  }

  const named = NAMED_SCOPES.get(segment);
  if (named != null) {
    return named;
  }

  if (isGuid(segment)) {
    return { type: VaultScopeType.Organization, organizationId: segment as OrganizationId };
  }

  return null;
}

/**
 * Whether every vault the account can reach is the personal one. Data ownership also leaves one
 * vault, but that one is an organization's, and personal items may still exist outside it.
 */
export function isPersonalOnly(nav: VaultsNavViewModel): boolean {
  return nav.vaults.length === 1 && !nav.organizationDataOwnership;
}

/**
 * The id of an organization's "My items" collection, as the nav holds it. `undefined` when the
 * account's vaults have yet to load, when the organization is not one of them, or when it has no
 * such collection — only organizations under the data ownership policy do.
 */
export function defaultUserCollectionId(
  organizationId: OrganizationId,
  nav: VaultsNavViewModel | undefined,
): CollectionId | undefined {
  return nav?.vaults.find(
    ({ id, type }) => type !== VaultNavItemType.Personal && id === organizationId,
  )?.defaultUserCollectionId;
}

/**
 * {@link parseVaultScope}, with the two segments only the nav can resolve given their answer:
 * All items becomes My vault for an {@link isPersonalOnly} account — the same destination for them,
 * so branching on the scope type gives one answer whichever of the two URLs they arrived by — and
 * a {@link MY_ITEMS_ROUTE} collection segment becomes the id of the organization's "My items"
 * collection, which is what every function narrowing by a collection compares against.
 *
 * `nav` is `undefined` until the account's vaults load. All items is the safe answer meanwhile,
 * since it shows a superset, and a "My items" segment stays as it came — resolving it needs the
 * nav, and the alternative, widening to the whole organization vault, would show items the URL did
 * not ask for.
 *
 * An organization with no "My items" collection names no destination by that segment, so it yields
 * `null` the way an unknown vault segment does.
 */
export function resolveVaultScope(
  segment: string | null | undefined,
  collectionSegment: string | null | undefined,
  nav: VaultsNavViewModel | undefined,
): VaultScope | null {
  const scope = parseVaultScope(segment, collectionSegment);

  if (scope?.type === VaultScopeType.AllItems && nav != null && isPersonalOnly(nav)) {
    return { type: VaultScopeType.MyVault };
  }

  if (scope?.type === VaultScopeType.Organization && scope.collectionId === MY_ITEMS_ROUTE) {
    if (nav == null) {
      return scope;
    }

    const collectionId = defaultUserCollectionId(scope.organizationId, nav);
    return collectionId == null ? null : { ...scope, collectionId };
  }

  return scope;
}

/**
 * The `Router.navigate` commands for a scope — the single place vault scope URLs are built, so
 * the nav and the route parser can't drift.
 */
export function vaultScopeCommands(scope: VaultScope): string[] {
  switch (scope.type) {
    case VaultScopeType.MyVault:
      return ["/vault", MY_VAULT_ROUTE];
    case VaultScopeType.Organization: {
      const { organizationId, collectionId } = scope;

      if (collectionId == null) {
        return ["/vault", organizationId];
      }

      // "My items" is its own page rather than a folder reached from the shared folders list, so
      // it hangs off the vault directly — see {@link MY_ITEMS_ROUTE}.
      return collectionId === MY_ITEMS_ROUTE
        ? ["/vault", organizationId, MY_ITEMS_ROUTE]
        : ["/vault", organizationId, SHARED_FOLDERS_ROUTE, collectionId];
    }
    case VaultScopeType.Trash:
      return ["/vault", TRASH_ROUTE];
    case VaultScopeType.Archive:
      return ["/vault", ARCHIVE_ROUTE];
    default:
      return ["/vault"];
  }
}

/**
 * The `Router.navigate` commands for an organization vault's shared folders list.
 *
 * Deliberately not a {@link VaultScope} member: a scope says which *items* a page shows, and
 * {@link cipherInScope}, {@link collectionInScope}, and {@link organizationInScope} would each have
 * to answer that for a page that lists folders instead. They switch on the scope type with a
 * `default` branch, so a new member would compile silently and fall through to All items' behavior.
 */
export function sharedFoldersCommands(organizationId: OrganizationId): string[] {
  return [
    ...vaultScopeCommands({ type: VaultScopeType.Organization, organizationId }),
    SHARED_FOLDERS_ROUTE,
  ];
}

/**
 * Cipher ids are branded SDK types on `CipherListView` but plain strings on `CipherView`, so an
 * organization id read off `CipherViewLike` needs widening before it can be compared.
 */
const idString = (id: unknown): string | undefined => (id == null ? undefined : String(id));

/**
 * The shared folder the scope has drilled into, or `undefined` when it covers a whole vault. Only
 * an organization vault can be drilled into — see {@link parseVaultScope}.
 *
 * A scope {@link resolveVaultScope} has yet to resolve names its folder by the
 * {@link MY_ITEMS_ROUTE} sentinel rather than by an id — see {@link ScopedCollectionId}.
 */
export function scopedSharedFolderId(scope: VaultScope): ScopedCollectionId | undefined {
  return scope.type === VaultScopeType.Organization ? scope.collectionId : undefined;
}

/** Whether the scope is the personal vault — drives the empty vault's "My vault" copy. */
export function isMyVaultScope(scope: VaultScope): boolean {
  return scope.type === VaultScopeType.MyVault;
}

/**
 * Whether the account has more than one vault (personal + at least one org) — drives the empty
 * vault's plural "Your vaults are empty" copy.
 */
export function hasMultipleVaults(nav: VaultsNavViewModel | undefined): boolean {
  return (nav?.vaults.length ?? 0) > 1;
}

/**
 * The organization the scope names, for the empty vault's "No items in {org}" copy: the nav's
 * label for `scope.organizationId`, or — for an organization-only account landing on the unscoped
 * route, which {@link resolveVaultScope} never turns into an {@link VaultScopeType.Organization}
 * scope — the nav's one organization, when it has no personal vault to be ambiguous with.
 */
export function organizationNameForScope(
  scope: VaultScope,
  nav: VaultsNavViewModel | undefined,
): string | undefined {
  if (!nav) {
    return undefined;
  }

  if (scope.type === VaultScopeType.Organization) {
    const named = nav.vaults.find((vault) => vault.id === scope.organizationId)?.label;
    if (named) {
      return named;
    }
  }

  const hasOnlyOneOrgVault =
    nav.vaults.length === 1 && nav.vaults[0].type !== VaultNavItemType.Personal;

  return hasOnlyOneOrgVault ? nav.vaults[0].label : undefined;
}

/**
 * The shared folder the scope has drilled into, by name — `undefined` unless the scope names a
 * collection {@link collections} can resolve. See {@link scopedSharedFolderId}.
 */
export function sharedFolderNameForScope(
  scope: VaultScope,
  collections: readonly CollectionView[],
): string | undefined {
  const collectionId = scopedSharedFolderId(scope);
  return collectionId
    ? collections.find((collection) => collection.id === collectionId)?.name
    : undefined;
}

/**
 * Whether a cipher belongs in the scope — the single place every dimension of a scope is decided,
 * so no caller has to pair a vault filter with a state filter and risk getting one of them wrong.
 *
 * Every vault scope shows active items only. Trash and Archive invert that and span every vault,
 * and a trashed item stays in Trash whether or not it was archived when it was deleted.
 *
 * A scope drilled into a shared folder keeps that folder's own items and no others: a child
 * folder's items arrive with the drill-in to the child.
 */
export function cipherInScope(cipher: CipherViewLike, scope: VaultScope): boolean {
  const deleted = CipherViewLikeUtils.isDeleted(cipher);
  const archived = CipherViewLikeUtils.isArchived(cipher);
  const organizationId = idString(cipher.organizationId);

  switch (scope.type) {
    case VaultScopeType.Trash:
      return deleted;
    case VaultScopeType.Archive:
      return archived && !deleted;
    case VaultScopeType.MyVault:
      return !deleted && !archived && organizationId == null;
    case VaultScopeType.Organization: {
      if (deleted || archived || organizationId !== scope.organizationId) {
        return false;
      }
      const { collectionId } = scope;
      return (
        collectionId == null ||
        (cipher.collectionIds ?? []).some((id) => idString(id) === collectionId)
      );
    }
    default:
      return !deleted && !archived;
  }
}

/**
 * Whether a collection belongs to the scoped vault. Trash and Archive span every vault, so they
 * keep the lot; the personal vault has no collections, so it keeps none.
 *
 * The vault dimension only — a scope drilled into a shared folder still keeps every collection its
 * organization owns, since an item in that folder may belong to others alongside it.
 */
export function collectionInScope(collection: CollectionView, scope: VaultScope): boolean {
  switch (scope.type) {
    case VaultScopeType.MyVault:
      return false;
    case VaultScopeType.Organization:
      return idString(collection.organizationId) === scope.organizationId;
    default:
      return true;
  }
}

/** Whether an organization owns the scoped vault. Mirrors {@link collectionInScope}. */
export function organizationInScope(organization: Organization, scope: VaultScope): boolean {
  switch (scope.type) {
    case VaultScopeType.MyVault:
      return false;
    case VaultScopeType.Organization:
      return idString(organization.id) === scope.organizationId;
    default:
      return true;
  }
}
